import { autoUpdate, flip, offset, shift, size, useDismiss, useFloating, useInteractions } from '@floating-ui/react';

import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ReactPortal from 'components/global/ReactPortal';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { Breakpoints, POPPER_CONFIG } from 'utils/const.styles';

import StringInput from './StringInput';
import * as S from './styled/AutoString.styled';

/**
 * @author @buunsal
 * @param {Object} props - props of the component.
 * @param {string} props.placeholder - placeholder of the input.
 * @param {string} props.value - value of the input.
 * @param {function} props.setValue - function to set value of the input.
 * @param {Array} props.columns - columns of the input.
 * @param {string} props.name - name of the input.
 * @param {string} props.errorMessage - error message of the input.
 * @param {boolean} props.disabled - if true, the input will be disabled.
 * @param {boolean} props.viewMode - if true, the input will be in view mode.
 * @param {Array} props.data - data of the input.
 * @param {function} props.fetchFunction - function to fetch data.
 * @param {boolean} props.fixedContainerWidth - if true, the container width will be fixed.
 * @param {string} props.labelPrefix - prefix of the label.
 * @param {function} props.setSelectedOption - function to set selected option.
 * @param {boolean} props.autoFilter - if true, the input will be filtered automatically.
 */
const AutoString = (props) => {
	const {
		placeholder,
		value,
		setValue,
		columns,
		name = '',
		errorMessage: errorMessageProp,
		disabled,
		viewMode,
		data,
		fetchFunction,
		fixedContainerWidth = true,
		labelPrefix,
		setSelectedOption,
		autoFilter = true
	} = props;

	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [localData, setLocalData] = useState(data);
	const [filteredData, setFilteredData] = useState(data);
	const isMobile = useMediaQuery(Breakpoints.mobile);

	useUpdateEffect(() => {
		setLocalData(data);
	}, [data]);

	useUpdateEffect(() => {
		setFilteredData(localData);
	}, [localData]);

	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	const middleware = useMemo(
		() => [
			flip({
				padding: 10
			}),
			shift({
				mainAxis: false
			}),
			offset({
				mainAxis: 8,
				crossAxis: 0
			}),
			size({
				apply({ availableHeight, rects, elements, x }) {
					if (fixedContainerWidth) {
						elements.floating.style.width = `${rects.reference.width}px`;
					} else if (isMobile) {
						const popperWidth = document.documentElement.clientWidth - 2 * POPPER_CONFIG.MARGIN;
						elements.floating.style.width = `${popperWidth}px`;
						elements.floating.style.left = `${POPPER_CONFIG.MARGIN - x}px`;
					}
					elements.floating.style.maxHeight = availableHeight
						? `min(33rem, calc(${availableHeight}px - 5.5rem))`
						: '33rem';
				}
			})
		],
		[fixedContainerWidth]
	);

	const { refs, context, floatingStyles } = useFloating({
		placement: 'bottom-start',
		open,
		onOpenChange: setOpen,
		middleware,
		whileElementsMounted: autoUpdate
	});

	const closePopper = () => {
		if (!open) return;
		setOpen(false);
	};

	const handleItemClick = (event, item) => {
		event.stopPropagation();

		if (columns) {
			setValue(item[name]);
			setSelectedOption(item);
		} else {
			setValue(t(`${labelPrefix ?? ''}${item}`));
		}
		closePopper();
	};

	const dismiss = useDismiss(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

	useEffect(() => {
		if (!autoFilter) return;

		if (value === null) {
			setFilteredData(localData);
		} else {
			if (columns) {
				setFilteredData(
					localData?.filter((item) =>
						columns.some((column) =>
							item[column?.accessor]?.toLocaleLowerCase().includes(value?.toLocaleLowerCase())
						)
					)
				);
			} else {
				setFilteredData(
					localData?.filter((item) =>
						t(`${labelPrefix ?? ''}${item}`)
							?.toLocaleLowerCase()
							.includes(value?.toLocaleLowerCase())
					)
				);
			}
		}
	}, [value]);

	return (
		<>
			<StringInput
				ref={refs.setReference}
				placeholder={placeholder}
				value={value}
				setValue={setValue}
				errorMessage={errorMessage}
				disabled={disabled}
				viewMode={viewMode}
				{...getReferenceProps({
					onFocus() {
						if (open) return;
						if (fetchFunction) {
							fetchFunction().then((data) => {
								setLocalData(data);
								setOpen(true);
							});
						} else {
							setOpen(true);
						}
					}
				})}
			/>
			{!disabled && open && filteredData?.length > 0 && (
				<ReactPortal wrapperId={'autostring-portal'}>
					<S.Container ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
						{filteredData.slice(0, 100).map((item, index) => (
							<S.Item
								key={index}
								onMouseDown={(event) => event.stopPropagation()}
								onTouchEnd={(event) => event.stopPropagation()}
								onClick={(event) => handleItemClick(event, item)}>
								{columns ? (
									columns.map(({ render, width, accessor }, i) => (
										<S.Cell key={`item_${i}`} width={width}>
											{render ? (
												render(item[accessor], item)
											) : (
												<Typography>{item[accessor]}</Typography>
											)}
										</S.Cell>
									))
								) : (
									<S.Cell>
										<Typography>{`${labelPrefix ?? ''}${item}`}</Typography>
									</S.Cell>
								)}
							</S.Item>
						))}
					</S.Container>
				</ReactPortal>
			)}
		</>
	);
};

export default AutoString;
